import { useLayoutEffect, useRef, useState } from 'react';

/** What a cut ends on: never a separator left dangling before the ellipsis ("Acme /…"). */
const TRAILING_SEPARATOR = /[\s/·,:;–—-]+$/;

/**
 * I-1345 (the captain's ruling for Jay, 2026-10-09): a one-line name that does not fit is cut at a
 * word, never inside one — "Customer onboarding…", not "Customer onboarding ex…" — with the whole
 * name as its tooltip. CSS cuts at a character, so the words that fit are measured: a hidden copy in
 * the same font, case and tracking takes each candidate, and the longest that fits beside "…" is
 * drawn. A first word wider than the row is left to the CSS ellipsis, the one cut nothing avoids.
 *
 * `lines={2}` (I-1677, IP-33): the name wraps at a word onto a second line, and only what two lines
 * cannot hold is cut, at a word, on the second. On the iPad's 44 px namespace line a one-line cut
 * dropped a whole word and left its width empty ("Shell Check…" with about 60 px unused). The copy
 * then wraps at the box's width, and a candidate fits when it is no taller than two lines.
 */
export function WordTruncate({
  text,
  className = '',
  title = text,
  lines = 1,
}: {
  text: string;
  className?: string;
  /** The tooltip; the whole text unless the caller names it otherwise. */
  title?: string;
  /** How many lines the text may take before it is cut: 1, or 2 where a line has the height. */
  lines?: 1 | 2;
}) {
  const boxRef = useRef<HTMLSpanElement | null>(null);
  const measureRef = useRef<HTMLSpanElement | null>(null);
  const [shown, setShown] = useState(text);

  useLayoutEffect(() => {
    const box = boxRef.current;
    const measure = measureRef.current;
    if (!box || !measure) return undefined;
    /*
     * F-0585: the room a one-line name has is the width its box takes holding the whole name. A cut
     * name's box can be as wide as its cut (the namespace heading's name sizes to its text), so
     * once cut, its own width never grew again when the sidebar did: "Shell Check…" at 480 px.
     */
    const roomOf = () => {
      const drawn = box.firstChild;
      if (lines !== 1 || !(drawn instanceof Text) || drawn.data === text) return box.clientWidth;
      const cut = drawn.data;
      drawn.data = text;
      const room = box.clientWidth;
      drawn.data = cut;
      return room;
    };
    let room = 0;
    const fits = (candidate: string) => {
      measure.textContent = candidate;
      if (lines === 1) return measure.getBoundingClientRect().width <= room + 0.5;
      const lineHeight = parseFloat(getComputedStyle(box).lineHeight);
      return measure.getBoundingClientRect().height <= lineHeight * lines + 0.5;
    };
    const fit = () => {
      room = roomOf();
      if (box.clientWidth === 0 || fits(text)) {
        setShown(text);
        return;
      }
      const words = text.split(/\s+/);
      let best = '';
      let low = 1;
      let high = words.length - 1;
      while (low <= high) {
        const count = Math.floor((low + high) / 2);
        const head = words.slice(0, count).join(' ').replace(TRAILING_SEPARATOR, '');
        if (head && fits(`${head}…`)) {
          best = `${head}…`;
          low = count + 1;
        } else {
          high = count - 1;
        }
      }
      setShown(best || text);
    };
    const fitAndClear = () => {
      fit();
      // The copy holds nothing once measured, so the row's text is only what it draws.
      measure.textContent = '';
    };
    fitAndClear();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(fitAndClear);
    observer.observe(box);
    // F-0585: the room changes where the line does, which a box sized to its cut does not follow.
    for (let at = box.parentElement, up = 0; at && up < 2; at = at.parentElement, up += 1) {
      observer.observe(at);
    }
    return () => observer.disconnect();
  }, [text, lines]);

  return (
    <span
      ref={boxRef}
      title={title}
      data-word-truncate=""
      data-word-truncate-lines={lines}
      className={`relative min-w-0 overflow-hidden text-ellipsis ${
        lines === 1
          ? 'block whitespace-nowrap'
          : 'whitespace-normal [-webkit-box-orient:vertical] [-webkit-line-clamp:2] [display:-webkit-box]'
      } ${className}`}
    >
      {shown}
      <span
        ref={measureRef}
        aria-hidden="true"
        className={`pointer-events-none invisible absolute left-0 top-0 ${
          lines === 1 ? 'whitespace-nowrap' : 'w-full whitespace-normal'
        }`}
      />
    </span>
  );
}
